import { flow } from '@1x2network/flow'
import { BasicLayout } from '@1x2network/layout'
import { BezierCurve, ParticleTrail, ParticleTrailSpecification } from '@1x2network/particle_trail'
import { Component, ContainerSpecification } from '@1x2network/pixi_assets'
import { PositionTuple, TradReelSet } from '@1x2network/reel_set'
import {
    createNudgeEffect,
    EffectManager,
    Shake2D,
    ShakeStartOptions,
    ShakeStopOptions,
    TweenVars,
} from '@1x2network/tweens'
import { pixiMathUtils } from '@1x2network/utils_pixi_math'
import gsap from 'gsap'
import { Container, ICanvas, IPointData, IRenderer, Point, Rectangle } from 'pixi.js'
import { Signal } from 'typed-signals'
import { CustomSymbol } from '../CustomSymbol'
import { SymbolModel } from '../model/SymbolModel'
import { ShapeOptions } from '../types/ShapeOptions'
import { getGameSizeInPixels } from '../utils/getGameSizeInPixels'
import { staggerTimeline } from '../utils/gsap'
import { makeShape } from '../utils/shapes/makeShape'
import { toSeconds } from '../utils/toSeconds'
import { FromToVars } from '@1x2network/tweens/lib/types/common'

const { projectRayToRectangleBounds } = pixiMathUtils

type CoinShowerArgs = {
    destination: Point
    transform: {
        position: PositionTuple
        data: SymbolModel
    }
}

/**
 * Animation to transform symbols on the board by having them shoot out of the logo
 * @author Adam Drake - <adam.drake@1x2network.com>
 * @version 1.0.0
 */
export class CoinShower extends Component<CoinShowerSpecification> {
    private reelSet: TradReelSet<CustomSymbol, SymbolModel>
    private shake: Shake2D
    private layout: BasicLayout
    private effectManager: EffectManager
    private cashSymbols: CustomSymbol[] = []
    private collectorSymbols: CustomSymbol[] = []
    private renderer: IRenderer<ICanvas>

    public readonly onAnticipate = new Signal()
    public readonly onEmit = new Signal()
    public readonly onShootOut = new Signal()
    public readonly onLand = new Signal()

    constructor(options: {
        parent?: Container
        layout: BasicLayout
        shake: Shake2D
        reelSet: TradReelSet<CustomSymbol, SymbolModel>
        effectManager: EffectManager
        renderer: IRenderer<ICanvas>
    }) {
        super({ spec: 'coinShower', parent: options.parent })
        this.layout = options.layout
        this.shake = options.shake
        this.reelSet = options.reelSet
        this.effectManager = options.effectManager
        this.renderer = options.renderer
    }

    public async animate(events: Array<CoinShowerArgs>): Promise<void> {
        const shakeStartSpec = this.specification.tweens.shakeStart
        const shakeStopSpec = this.specification.tweens.shakeStop
        const stagger = toSeconds(this.specification.shootOutAnimation.stagger)

        await flow.wait(400)
        this.onAnticipate.emit()
        this.shake.start(shakeStartSpec)
        await flow.wait(1200)
        this.shake.stop(shakeStopSpec)
        await flow.wait(150)
        this.onEmit.emit()

        const shootOutAnimations = events.map((e, i) => this.animateShootOut(e, i))
        const landAnimations = events.map((e, i) => this.animateLand(e, i))

        const animation = shootOutAnimations.map((_, i) => {
            const shootOut = shootOutAnimations[i]
            const land = landAnimations[i]
            return gsap.timeline().add(shootOut).add(land, `+=${1}`)
        })

        await staggerTimeline(animation, stagger)
        await flow.wait(150)
    }

    private animateShootOut(options: CoinShowerArgs, i: number): GSAPAnimation {
        const { transform } = options
        const shootOutAnimation = this.specification.shootOutAnimation
        const gameSize = getGameSizeInPixels(this.renderer)

        const symbol = this.reelSet.pool.requestLandSymbol(transform.data)
        symbol.zIndex = 3
        symbol.blurred = false
        symbol.scale.set(0.5)
        this.cashSymbols[i] = symbol

        const topLeft = this.toLocal(new Point(0, 0))
        const bottomRight = this.toLocal(new Point(gameSize.width, gameSize.height))
        const rect = new Rectangle(topLeft.x, topLeft.y, bottomRight.x - topLeft.x, bottomRight.y - topLeft.y)
        const startPosition = new Point().copyFrom(shootOutAnimation.startPosition[this.layout.orientation])
        const rayDirection = new Point(1, -1.2)
        const endPosition = projectRayToRectangleBounds(
            startPosition,
            rayDirection,
            rect.pad(symbol.width / 2, symbol.height / 2),
        )

        const { trailSpec } = this.specification
        const { steps, tweenVars, shape: shapeOptions } = shootOutAnimation
        const shape = makeShape(shapeOptions.kind, shapeOptions.options)
        const points = shape.calculateControlPoints(startPosition, endPosition)
        const bezier = new BezierCurve(points, steps)
        const trail = new ParticleTrail(bezier, trailSpec)

        return trail.animate({
            ...tweenVars,
            onParticlesComplete: () => {
                trail.destroy()
            },
            onUpdate: (point) => {
                symbol.position.copyFrom(point)
            },
            onStart: () => {
                this.onShootOut.emit()
                symbol.playCollect()
                this.reelSet.addChild(trail, symbol)
            },
        })
    }

    private animateLand(options: CoinShowerArgs, i: number): GSAPAnimation {
        const { destination } = options
        const trailAnim = this.specification.trailAnim
        const symbol = this.cashSymbols[i]
        const gameSize = getGameSizeInPixels(this.renderer)

        const topLeft = this.toLocal(new Point(0, 0))
        const bottomRight = this.toLocal(new Point(gameSize.width, gameSize.height))
        const rect = new Rectangle(topLeft.x, topLeft.y, bottomRight.x - topLeft.x, bottomRight.y - topLeft.y)
        const endPosition = destination
        const rayDirection = new Point(1, -1)
        const startPosition = projectRayToRectangleBounds(
            endPosition,
            rayDirection,
            rect.pad(symbol.width / 2, symbol.height / 2),
        )

        const { trailSpec } = this.specification
        const { steps, tweenVars, shape: shapeOptions } = trailAnim
        const shape = makeShape(shapeOptions.kind, shapeOptions.options)
        const points = shape.calculateControlPoints(startPosition, endPosition)
        const bezier = new BezierCurve(points, steps)
        const trail = new ParticleTrail(bezier, trailSpec)

        const trailTween = trail.animate({
            ...tweenVars,
            onParticlesComplete: () => {
                trail.destroy()
            },
            onUpdate: (point) => {
                symbol.position.copyFrom(point)
            },
            onStart: () => {
                symbol.playWin()
                this.addChild(trail, symbol)
            },
            onComplete: () => {
                this.reelSet.topContainer.addChild(trail, symbol)
            },
        })

        const timeline = gsap
            .timeline()
            .add(trailTween)
            .to(symbol.scale, { x: 1, y: 1, duration: 0.15, ease: 'back.out(1.7)' })
            .add(
                this.effectManager.addAnimation(
                    this.reelSet,
                    createNudgeEffect({ targetOffset: { y: 5 }, duration: 0.25 }),
                ),
                '<',
            )

        timeline.call(
            () => {
                this.onLand.emit()
            },
            [],
            '<',
        )

        return timeline
    }

    public reset(): void {
        this.collectorSymbols.forEach((symbol) => {
            this.reelSet.pool.returnSymbol(symbol)
        })
        this.cashSymbols.forEach((symbol) => {
            this.reelSet.pool.returnSymbol(symbol)
        })
        this.cashSymbols = []
        this.collectorSymbols = []
    }
}
export interface CoinShowerSpecification extends ContainerSpecification {
    trailSpec: ParticleTrailSpecification
    shootOutAnimation: {
        startPosition: { landscape: IPointData; portrait: IPointData }
        shape: ShapeOptions
        steps: number
        tweenVars: TweenVars
        stagger: number
    }
    trailAnim: {
        shape: ShapeOptions
        steps: number
        tweenVars: TweenVars
    }
    tweens: {
        shakeStart: ShakeStartOptions
        shakeStop: ShakeStopOptions
        collectorFadeFromTo: FromToVars
    }
}
